/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useState,
  useContext,
  Suspense,
  lazy,
  useCallback,
  useMemo,
} from "react";
import {
  Grid,
  Card,
  Typography,
  Box,
  Skeleton,
  CircularProgress,
  Fade,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../../services/api";
import { getToken } from "../../../services/auth";
import { startCameraHls, stopCameraHls } from "../../../services/cameraHls";
import { fetchCameraStatusShared } from "../../../services/cameraStatus";
import { useIntl } from "../../../contexts/IntlContext";
import ConfirmDialog from "../../../components/ConfirmDialog";
import DeletionPreviewDialog, {
  DeletionPreviewRow,
} from "../../../components/DeletionPreviewDialog";
import LoadingButton from "../../../components/ui/LoadingButton";
import IconButton from "../../../components/ui/IconButton";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { hasOperationalAccess } from "../../../utils/authRole";
import { useNavigate } from "react-router-dom";

import { AuthContext } from "../../../contexts/AuthContext";
import { useCountry } from "../../../contexts/CountryContext";
import StatusToggle from "../../../components/StatusToggle";
import StatusPill from "../../../components/ui/StatusPill";
import { PageHeader } from "../../../components/PageHeader";
import { fontWeights } from "../../../themes/fontWeights";

const CamerasFilters = lazy(() => import("../../../components/CamerasFilters"));

const CameraCard = lazy(() => import("../../../components/CameraCard"));
const CameraPreviewModal = lazy(
  () => import("../../../components/CameraPreviewModal")
);

type Channel = {
  id: string;
  name: string;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  offlineSince?: string;
  deviceCardKey?: string;
  deviceId?: number;
  deviceLabel?: string;
  online?: boolean;
  frameSrc?: string | null; // data:url or blob URL for thumbnail preview
};

type EstablishmentWithChannels = {
  id: string;
  name: string;
  state?: string;
  city?: string;
  isPrivate?: boolean;
  status?: boolean;
  createdAt?: string;
  updatedAt?: string;
  channels: Channel[];
};

type TLegacyPreviewResponse = {
  devices?: Array<{
    deviceId?: number;
    deviceName?: string;
    legacyPreviewUrl?: string;
    username?: string;
  }>;
};

type TLegacyPreviewByChannel = Record<
  string,
  {
    legacyPreviewUrl: string;
    username?: string;
    deviceId?: number;
    deviceName?: string;
  }
>;

type TCameraStatusRow = {
  establishmentId?: string;
  deviceId?: number;
  deviceName?: string;
  channelUuid?: string;
  channelName?: string;
  online?: boolean;
  lastCommunicationCheckDateTime?: string;
  lastCommunicationSuccessDateTime?: string;
  lastEventDateTime?: string;
};

export default function CamerasList() {
  const intl = useIntl();
  const { country } = useCountry();

  const navigate = useNavigate();
  const { authInfo } = useContext(AuthContext);
  const canOperate = hasOperationalAccess(authInfo?.role);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<EstablishmentWithChannels[]>([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [onlineFilter, setOnlineFilter] = useState<"" | "online" | "offline">("");
  // default sort: created_desc => newest first
  const [sortValue, setSortValue] = useState<string | undefined>(
    "created_desc"
  );

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [totalCount, setTotalCount] = useState(0);

  type ConfirmState =
    | { type: "deleteEst"; id: string; name: string }
    | { type: "deleteChannel"; estId: string; channelId: string; name: string }
    | {
        type: "deleteDevice";
        estId: string;
        deviceId: number;
        channelId: string;
        name: string;
      };

  const [openChannelPreview, setOpenChannelPreview] = useState<{
    estId: string;
    channelId: string;
    name: string;
    deviceId?: number;
  } | null>(null);
  const [openChannelPreviewHlsSrc, setOpenChannelPreviewHlsSrc] = useState<
    string | undefined
  >(undefined);
  const [openChannelPreviewStartingHls, setOpenChannelPreviewStartingHls] =
    useState(false);

  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [deletionSummary, setDeletionSummary] = useState<{
    loading: boolean;
    error: string | null;
    data: {
      channelsCount?: number;
      videosCount: number;
      logosCount: number;
      favoritesCount: number;
      savedVideosCount: number;
    } | null;
  }>({ loading: false, error: null, data: null });

  useEffect(() => {
    if (!confirm || (confirm.type !== "deleteEst" && confirm.type !== "deleteChannel")) {
      return;
    }

    let cancelled = false;
    setDeletionSummary({ loading: true, error: null, data: null });

    const url =
      confirm.type === "deleteEst"
        ? `/app/establishments/${confirm.id}/deletion-summary`
        : `/app/establishments/${confirm.estId}/channels/${confirm.channelId}/deletion-summary`;

    api
      .get(url)
      .then((res) => {
        if (cancelled) return;
        setDeletionSummary({ loading: false, error: null, data: res.data });
      })
      .catch(() => {
        if (cancelled) return;
        setDeletionSummary({
          loading: false,
          error: intl.formatMessage({ id: "deletionPreview.loadError" }),
          data: null,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [confirm]);

  const deletionPreviewRows = useMemo<DeletionPreviewRow[]>(() => {
    if (!deletionSummary.data) return [];
    const rows: DeletionPreviewRow[] = [];
    if (confirm?.type === "deleteEst" && typeof deletionSummary.data.channelsCount === "number") {
      rows.push({
        label: intl.formatMessage({ id: "deletionPreview.rowChannels" }),
        value: deletionSummary.data.channelsCount,
      });
    }
    rows.push(
      {
        label: intl.formatMessage({ id: "deletionPreview.rowVideos" }),
        value: deletionSummary.data.videosCount,
      },
      {
        label: intl.formatMessage({ id: "deletionPreview.rowLogos" }),
        value: deletionSummary.data.logosCount,
      },
      {
        label: intl.formatMessage({ id: "deletionPreview.rowFavorites" }),
        value: deletionSummary.data.favoritesCount,
      },
      {
        label: intl.formatMessage({ id: "deletionPreview.rowSavedVideos" }),
        value: deletionSummary.data.savedVideosCount,
      }
    );
    return rows;
  }, [deletionSummary.data, confirm?.type, intl]);
  const [playingChannelKey, setPlayingChannelKey] = useState<string | null>(
    null
  );
  const [loadingPreviewChannelKey, setLoadingPreviewChannelKey] = useState<
    string | null
  >(null);
  const [legacyPreviewByChannel, setLegacyPreviewByChannel] =
    useState<TLegacyPreviewByChannel>({});
  const playingChannelKeyRef = React.useRef<string | null>(null);
  const streamFallbackInFlightRef = React.useRef<Set<string>>(new Set());
  const legacyPreviewByChannelRef = React.useRef<TLegacyPreviewByChannel>({});
  const legacyPreviewInFlightRef = React.useRef<Set<string>>(new Set());
  const noVideoWarnedAtRef = React.useRef<Map<string, number>>(new Map());
  const openChannelPreviewRequestRef = React.useRef(0);
  const openChannelPreviewSessionIdRef = React.useRef<string | null>(null);
  const loadRequestIdRef = React.useRef(0);
  const loadAbortControllerRef = React.useRef<AbortController | null>(null);
  const snapshotAbortControllersRef = React.useRef<
    Map<string, AbortController>
  >(new Map());

  const firstLoadRef = React.useRef(true);

  const normalizeKeyLoose = useCallback((value: string) => {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  }, []);

  const getChannelKey = (
    establishmentId: string,
    channelId: string,
    deviceId?: number
  ) => `${establishmentId}::${channelId}::${deviceId ?? "na"}`;

  const getChannelStreamSrc = (
    establishmentId: string,
    channelId: string,
    deviceId?: number
  ) => {
    const token = getToken();
    const params = new URLSearchParams();
    if (token) params.set("token", token);
    if (Number.isFinite(Number(deviceId)) && Number(deviceId) > 0) {
      params.set("deviceId", String(deviceId));
    }
    params.set("t", String(Date.now()));
    return `/app/establishments/${establishmentId}/channels/${channelId}/stream?${params.toString()}`;
  };

  const setChannelFrameSrc = useCallback(
    (
      establishmentId: string,
      channelId: string,
      frameSrc: string | null,
      deviceId?: number
    ) => {
      setItems((prev) =>
        prev.map((est) =>
          est.id !== establishmentId
            ? est
            : {
                ...est,
                channels: est.channels.map((ch) =>
                  ch.id === channelId &&
                  (Number.isFinite(Number(deviceId))
                    ? Number(ch.deviceId) === Number(deviceId)
                    : !Number.isFinite(Number(ch.deviceId)))
                    ? { ...ch, frameSrc }
                    : ch
                ),
              }
        )
      );
    },
    []
  );

  useEffect(() => {
    playingChannelKeyRef.current = playingChannelKey;
  }, [playingChannelKey]);

  useEffect(() => {
    legacyPreviewByChannelRef.current = legacyPreviewByChannel;
  }, [legacyPreviewByChannel]);

  const isRequestCanceled = useCallback((error: unknown) => {
    const code = String((error as any)?.code ?? "");
    const name = String((error as any)?.name ?? "");
    return (
      code === "ERR_CANCELED" ||
      name === "CanceledError" ||
      name === "AbortError"
    );
  }, []);

  const abortAllSnapshotRequests = useCallback(() => {
    snapshotAbortControllersRef.current.forEach((controller) => {
      try {
        controller.abort();
      } catch {
        // ignore
      }
    });
    snapshotAbortControllersRef.current.clear();
    setLoadingPreviewChannelKey(null);
  }, []);

  const abortCurrentLoadRequest = useCallback(() => {
    const controller = loadAbortControllerRef.current;
    loadAbortControllerRef.current = null;
    if (!controller) return;
    try {
      controller.abort();
    } catch {
      // ignore
    }
  }, []);

  const fetchChannelLegacyPreview = useCallback(
    async (establishmentId: string, channelId: string) => {
      const key = getChannelKey(establishmentId, channelId);
      const cached = legacyPreviewByChannelRef.current[key];
      if (cached?.legacyPreviewUrl) return cached;
      if (legacyPreviewInFlightRef.current.has(key)) return null;

      legacyPreviewInFlightRef.current.add(key);
      try {
        const response = await api.get<TLegacyPreviewResponse>(
          `/app/cameras/${encodeURIComponent(
            establishmentId
          )}/channels/${encodeURIComponent(channelId)}/legacy-preview-url`
        );

        const devices = Array.isArray(response.data?.devices)
          ? response.data.devices
          : [];

        const picked = devices.find(
          (device) => typeof device?.legacyPreviewUrl === "string"
        );
        const legacyPreviewUrl = String(picked?.legacyPreviewUrl ?? "").trim();
        if (!legacyPreviewUrl) return null;

        const resolved = {
          legacyPreviewUrl,
          username:
            typeof picked?.username === "string" ? picked.username : undefined,
          deviceId:
            Number.isFinite(Number(picked?.deviceId)) && Number(picked?.deviceId) > 0
              ? Number(picked?.deviceId)
              : undefined,
          deviceName:
            typeof picked?.deviceName === "string" ? picked.deviceName : undefined,
        };

        setLegacyPreviewByChannel((previous) => {
          if (previous[key]?.legacyPreviewUrl) return previous;
          return { ...previous, [key]: resolved };
        });

        return resolved;
      } catch {
        return null;
      } finally {
        legacyPreviewInFlightRef.current.delete(key);
      }
    },
    []
  );

  const openChannelLegacyPreview = useCallback(
    async (establishmentId: string, channelId: string) => {
      const key = getChannelKey(establishmentId, channelId);
      const cached = legacyPreviewByChannelRef.current[key];
      const resolved = cached?.legacyPreviewUrl
        ? cached
        : await fetchChannelLegacyPreview(establishmentId, channelId);

      if (!resolved?.legacyPreviewUrl) {
        toast.info(intl.formatMessage({ id: "cameras.legacyPreviewUnavailable" }));
        return;
      }

      window.open(resolved.legacyPreviewUrl, "_blank", "noopener,noreferrer");
    },
    [fetchChannelLegacyPreview, intl]
  );

  const closeChannelPreviewModal = useCallback(() => {
    setOpenChannelPreview(null);
    setOpenChannelPreviewHlsSrc(undefined);
    setOpenChannelPreviewStartingHls(false);
    openChannelPreviewRequestRef.current += 1;

    const sessionId = openChannelPreviewSessionIdRef.current;
    openChannelPreviewSessionIdRef.current = null;
    if (sessionId) {
      void stopCameraHls(sessionId);
    }
  }, []);

  const openChannelPreviewModal = useCallback(
    async (
      estId: string,
      channelId: string,
      name: string,
      preferredDeviceIdFromCard?: number
    ) => {
      const requestId = openChannelPreviewRequestRef.current + 1;
      openChannelPreviewRequestRef.current = requestId;

      const key = getChannelKey(estId, channelId, preferredDeviceIdFromCard);
      const cachedLegacy = legacyPreviewByChannelRef.current[key];
      const resolvedLegacy =
        cachedLegacy?.legacyPreviewUrl
          ? cachedLegacy
          : await fetchChannelLegacyPreview(estId, channelId);
      const preferredDeviceId =
        Number.isFinite(Number(preferredDeviceIdFromCard)) &&
        Number(preferredDeviceIdFromCard) > 0
          ? Number(preferredDeviceIdFromCard)
          : Number.isFinite(Number(resolvedLegacy?.deviceId)) &&
              Number(resolvedLegacy?.deviceId) > 0
            ? Number(resolvedLegacy?.deviceId)
          : undefined;

      setOpenChannelPreview({ estId, channelId, name, deviceId: preferredDeviceId });
      setOpenChannelPreviewHlsSrc(undefined);
      setOpenChannelPreviewStartingHls(true);

      try {
        const started = await startCameraHls({
          establishmentId: estId,
          channelId,
          deviceId: preferredDeviceId,
        });

        if (openChannelPreviewRequestRef.current !== requestId) {
          void stopCameraHls(started.sessionId);
          return;
        }

        const previousSessionId = openChannelPreviewSessionIdRef.current;
        openChannelPreviewSessionIdRef.current = started.sessionId;
        setOpenChannelPreviewHlsSrc(started.manifestUrl);

        if (previousSessionId && previousSessionId !== started.sessionId) {
          void stopCameraHls(previousSessionId);
        }
      } catch (error: any) {
        if (openChannelPreviewRequestRef.current === requestId) {
          const message =
            error?.response?.status === 409
              ? error?.response?.data?.message ||
                intl.formatMessage({ id: "cameras.streamReplaced" })
              : intl.formatMessage({ id: "cameras.streamUnavailable" });
          toast.warn(message);
        }
      } finally {
        if (openChannelPreviewRequestRef.current === requestId) {
          setOpenChannelPreviewStartingHls(false);
        }
      }
    },
    [fetchChannelLegacyPreview, intl]
  );

  const openChannelPreviewStreamSrc = useMemo(() => {
    if (!openChannelPreview) return undefined;
    const token = getToken();
    const params = new URLSearchParams();
    if (token) params.set("token", token);
    if (
      Number.isFinite(Number(openChannelPreview.deviceId)) &&
      Number(openChannelPreview.deviceId) > 0
    ) {
      params.set("deviceId", String(openChannelPreview.deviceId));
    }
    params.set("t", String(Date.now()));
    return `/app/establishments/${openChannelPreview.estId}/channels/${openChannelPreview.channelId}/stream?${params.toString()}`;
  }, [openChannelPreview]);

  useEffect(() => {
    return () => {
      const sessionId = openChannelPreviewSessionIdRef.current;
      openChannelPreviewSessionIdRef.current = null;
      if (sessionId) {
        void stopCameraHls(sessionId);
      }
      abortCurrentLoadRequest();
      abortAllSnapshotRequests();
    };
  }, [abortAllSnapshotRequests, abortCurrentLoadRequest]);

  const refreshChannelFrame = useCallback(
    async (
      establishmentId: string,
      channelId: string,
      options?: {
        noCache?: boolean;
        showLoading?: boolean;
        requestId?: number;
        deviceId?: number;
      }
    ) => {
      const noCache = !!options?.noCache;
      const showLoading = !!options?.showLoading;
      const requestId = Number(options?.requestId);
      const deviceId =
        Number.isFinite(Number(options?.deviceId)) && Number(options?.deviceId) > 0
          ? Number(options?.deviceId)
          : undefined;
      const key = getChannelKey(establishmentId, channelId, deviceId);
      const previousController = snapshotAbortControllersRef.current.get(key);
      if (previousController) {
        try {
          previousController.abort();
        } catch {
          // ignore
        }
      }
      const controller = new AbortController();
      snapshotAbortControllersRef.current.set(key, controller);

      if (showLoading) setLoadingPreviewChannelKey(key);

      const query = new URLSearchParams();
      query.set("liveOnly", "1");
      if (deviceId) {
        query.set("deviceId", String(deviceId));
      }
      if (noCache) {
        query.set("t", String(Date.now()));
      }
      const endpoint = `/app/establishments/${establishmentId}/channels/${channelId}/snapshot?${query.toString()}`;

      try {
        try {
          const resJson = await api.get(endpoint, { signal: controller.signal });
          if (resJson.data && resJson.data.thumbnail) {
            // Do not override active stream preview with a static snapshot.
            if (playingChannelKeyRef.current === key) {
              return true;
            }
            if (requestId && loadRequestIdRef.current !== requestId) {
              return false;
            }
            setChannelFrameSrc(
              establishmentId,
              channelId,
              `data:image/jpeg;base64,${resJson.data.thumbnail}`,
              deviceId
            );
            return true;
          }
        } catch (error) {
          if (isRequestCanceled(error)) {
            return false;
          }
          // fallback to blob below
        }

        const resBlob = await api.get(endpoint, {
          responseType: "blob",
          signal: controller.signal,
        });
        if (
          resBlob.data &&
          resBlob.data.type &&
          resBlob.data.type.startsWith("image")
        ) {
          // Do not override active stream preview with a static snapshot.
          if (playingChannelKeyRef.current === key) {
            return true;
          }
          if (requestId && loadRequestIdRef.current !== requestId) {
            return false;
          }
          const blobUrl = URL.createObjectURL(resBlob.data);
          setChannelFrameSrc(establishmentId, channelId, blobUrl, deviceId);
          return true;
        }
      } catch (error) {
        if (isRequestCanceled(error)) {
          return false;
        }
        // keep silent for automatic refresh; still allow toggling another camera
      } finally {
        const currentController = snapshotAbortControllersRef.current.get(key);
        if (currentController === controller) {
          snapshotAbortControllersRef.current.delete(key);
        }
        setLoadingPreviewChannelKey((prev) => (prev === key ? null : prev));
      }

      return false;
    },
    [isRequestCanceled, setChannelFrameSrc]
  );

  const handleChannelStreamError = useCallback(
    async (establishmentId: string, channelId: string, deviceId?: number) => {
      const key = getChannelKey(establishmentId, channelId, deviceId);
      if (streamFallbackInFlightRef.current.has(key)) return;
      streamFallbackInFlightRef.current.add(key);

      try {
        if (playingChannelKeyRef.current === key) {
          setPlayingChannelKey(null);
        }
        const lastWarnedAt = noVideoWarnedAtRef.current.get(key) ?? 0;
        const now = Date.now();
        if (now - lastWarnedAt > 10000) {
          noVideoWarnedAtRef.current.set(key, now);
          toast.warn(intl.formatMessage({ id: "cameras.streamUnavailable" }));
        }
        void fetchChannelLegacyPreview(establishmentId, channelId);
        await refreshChannelFrame(establishmentId, channelId, {
          noCache: true,
          showLoading: false,
          deviceId,
        });
      } finally {
        streamFallbackInFlightRef.current.delete(key);
      }
    },
    [fetchChannelLegacyPreview, intl, refreshChannelFrame]
  );

  useEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }

    const timer = setTimeout(() => {
      // reset to first page when filters/search change
      setPage(0);
      load();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, stateFilter, cityFilter, country, statusFilter, sortValue]);

  // load when page or rowsPerPage change (no debounce)
  useEffect(() => {
    if (firstLoadRef.current) return;
    load();
  }, [page, rowsPerPage]);

  async function load() {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    abortCurrentLoadRequest();
    abortAllSnapshotRequests();
    const controller = new AbortController();
    loadAbortControllerRef.current = controller;
    setLoading(true);
    try {
      const response = await api.get("/app/channels", {
        signal: controller.signal,
        params: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          country,
          search: search.trim().length > 0 ? search : undefined,
          state: stateFilter.trim().length > 0 ? stateFilter : undefined,
          city: cityFilter.trim().length > 0 ? cityFilter : undefined,
          sort: sortValue && sortValue.length > 0 ? sortValue : undefined,
        },
      });

      const data = response.data.data || [];
      const total = response.data.count ?? 0;
      if (loadRequestIdRef.current !== requestId) return;

      const mapped: EstablishmentWithChannels[] = data.map((est: any) => ({
        id: est.id,
        name: est.name,
        state: est.state,
        city: est.city,
        isPrivate: !!est.isPrivate,
        status: typeof est.status !== "undefined" ? !!est.status : true,
        createdAt: est.createdAt,
        updatedAt: est.updatedAt,
        channels: Array.isArray(est.channels)
          ? est.channels.map((c: any) => ({
              id: c.id,
              name: c.name,
              active: !!c.active,
              createdAt: c.createdAt,
              updatedAt: c.updatedAt,
            }))
          : [],
      }));

      const establishmentIds = mapped
        .map((est) => String(est.id ?? "").trim())
        .filter(Boolean);
      let statusRows: TCameraStatusRow[] = [];

      if (establishmentIds.length > 0) {
        try {
          const statusResponse = await fetchCameraStatusShared({
            establishmentIds,
          });
          if (loadRequestIdRef.current !== requestId) return;
          statusRows = Array.isArray(statusResponse?.data)
            ? (statusResponse.data as TCameraStatusRow[])
            : [];
        } catch {
          statusRows = [];
        }
      }

      const statusByEstablishment = new Map<string, TCameraStatusRow[]>();
      for (const row of statusRows) {
        const establishmentId = String(row?.establishmentId ?? "").trim();
        if (!establishmentId) continue;
        const currentRows = statusByEstablishment.get(establishmentId) ?? [];
        currentRows.push(row);
        statusByEstablishment.set(establishmentId, currentRows);
      }

      const matchesChannelByName = (channelName: string, row: TCameraStatusRow) => {
        const normalizedChannel = normalizeKeyLoose(channelName);
        if (!normalizedChannel) return false;

        const normalizedChannelName = normalizeKeyLoose(
          String(row?.channelName ?? "")
        );
        const normalizedDeviceName = normalizeKeyLoose(
          String(row?.deviceName ?? "")
        );

        if (
          normalizedChannelName &&
          (normalizedChannelName === normalizedChannel ||
            normalizedChannelName.includes(normalizedChannel) ||
            normalizedChannel.includes(normalizedChannelName))
        ) {
          return true;
        }

        if (
          normalizedDeviceName &&
          (normalizedDeviceName === normalizedChannel ||
            normalizedDeviceName.includes(normalizedChannel) ||
            normalizedChannel.includes(normalizedDeviceName))
        ) {
          return true;
        }

        return false;
      };

      const enrichedItems = mapped
        .map((establishment) => {
          const establishmentStatusRows =
            statusByEstablishment.get(establishment.id) ?? [];

          const expandedChannels = establishment.channels.flatMap((channel) => {
            const channelId = String(channel.id ?? "").trim();

            const directMatches = establishmentStatusRows.filter(
              (row) => String(row?.channelUuid ?? "").trim() === channelId
            );
            const fallbackMatches = establishmentStatusRows.filter((row) => {
              const rowChannelUuid = String(row?.channelUuid ?? "").trim();
              // If backend already links to another channel, do not steal it by name.
              if (rowChannelUuid && rowChannelUuid !== channelId) return false;
              return matchesChannelByName(String(channel.name ?? ""), row);
            });
            const rowsToUse = [...directMatches];
            for (const row of fallbackMatches) {
              const parsedDeviceId = Number(row?.deviceId);
              const alreadyIncluded =
                Number.isFinite(parsedDeviceId) && parsedDeviceId > 0
                  ? rowsToUse.some(
                      (directRow) =>
                        Number(directRow?.deviceId) === parsedDeviceId
                    )
                  : false;
              if (!alreadyIncluded) {
                rowsToUse.push(row);
              }
            }

            if (!rowsToUse.length) {
              return [
                {
                  ...channel,
                  deviceCardKey: `${channelId}::base`,
                },
              ];
            }

            const uniqueByDevice = new Map<number, TCameraStatusRow>();
            for (const row of rowsToUse) {
              const parsedDeviceId = Number(row?.deviceId);
              if (!Number.isFinite(parsedDeviceId) || parsedDeviceId <= 0) continue;
              if (!uniqueByDevice.has(parsedDeviceId)) {
                uniqueByDevice.set(parsedDeviceId, row);
              }
            }

            const deviceRows = Array.from(uniqueByDevice.entries());
            if (!deviceRows.length) {
              return [
                {
                  ...channel,
                  deviceCardKey: `${channelId}::base`,
                },
              ];
            }

            return deviceRows.map(([deviceId, row]) => {
              const deviceLabel = String(row?.deviceName ?? "").trim();
              return {
                ...channel,
                // Mantém o nome cadastrado pelo usuário como identificação principal
                // do card; o nome técnico do dispositivo vira apenas um subtítulo.
                name: channel.name,
                deviceLabel:
                  deviceLabel && deviceLabel !== channel.name ? deviceLabel : undefined,
                deviceCardKey: `${channelId}::${deviceId}`,
                deviceId,
                online: !!row?.online,
                offlineSince:
                  String(
                    row?.lastCommunicationSuccessDateTime ??
                      row?.lastEventDateTime ??
                      row?.lastCommunicationCheckDateTime ??
                      ""
                  ).trim() || undefined,
                updatedAt:
                  String(
                    row?.lastCommunicationCheckDateTime ??
                      row?.lastCommunicationSuccessDateTime ??
                      row?.lastEventDateTime ??
                      channel.updatedAt ??
                      ""
                  ).trim() || channel.updatedAt,
              };
            });
          });

          const channels =
            statusFilter === "active"
              ? expandedChannels.filter((channel) => channel.online !== false)
              : expandedChannels;

          return {
            ...establishment,
            channels,
          };
        })
        .filter((establishment) => establishment.channels.length > 0);

      setItems(enrichedItems);
      setTotalCount(Number(total));
      setLoading(false);

      const hasPlayingChannel =
        !!playingChannelKey &&
        enrichedItems.some((est) =>
          est.channels.some(
            (ch) => getChannelKey(est.id, ch.id, ch.deviceId) === playingChannelKey
          )
        );
      if (!hasPlayingChannel) {
        setPlayingChannelKey(null);
      }

      void Promise.allSettled(
        enrichedItems.flatMap((est) =>
          est.channels
            .filter((ch) => ch.online !== false)
            .map((ch) =>
              refreshChannelFrame(est.id, ch.id, {
                noCache: false,
                showLoading: false,
                requestId,
                deviceId: ch.deviceId,
              })
            )
        )
      ).then(() => {
        if (loadRequestIdRef.current !== requestId) return;

        if (hasPlayingChannel && playingChannelKey) {
          const [establishmentId, channelId, rawDeviceId] =
            playingChannelKey.split("::");
          const parsedDeviceId = Number(rawDeviceId);
          const deviceId =
            Number.isFinite(parsedDeviceId) && parsedDeviceId > 0
              ? parsedDeviceId
              : undefined;
          if (establishmentId && channelId) {
            setChannelFrameSrc(
              establishmentId,
              channelId,
              getChannelStreamSrc(establishmentId, channelId, deviceId),
              deviceId
            );
          }
        }
      });
    } catch (err) {
      if (loadRequestIdRef.current === requestId && !isRequestCanceled(err)) {
        toast.error(intl.formatMessage({ id: "cameras.fetchError" }));
      }
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
      if (loadAbortControllerRef.current === controller) {
        loadAbortControllerRef.current = null;
      }
    }
  }

  async function handleDeleteEstablishment(estId: string) {
    try {
      await api.delete(`/app/establishments/${estId}`);
      toast.success(
        intl.formatMessage({ id: "establishmentList.deleteSuccess" })
      );
      // Remove from items
      setItems((prev) => prev.filter((e) => e.id !== estId));
    } catch (err) {
      toast.error(intl.formatMessage({ id: "messages.connectionError" }));
    }
  }

  const handleToggleEstablishmentStatus = useCallback(
    async (estId: string, currentStatus: boolean) => {
      const nextStatus = !currentStatus;
      setItems((prev) =>
        prev.map((e) => {
          if (e.id !== estId) return e;
          return {
            ...e,
            status: nextStatus,
            channels:
              nextStatus === false
                ? e.channels.map((ch) => ({ ...ch, active: false }))
                : e.channels,
          };
        })
      );
      try {
        await api.put(`app/establishments/${estId}/updateStatus`, {
          status: nextStatus,
        });
        toast.success(
          intl.formatMessage({ id: "establishmentList.statusUpdateSuccess" })
        );
      } catch {
        setItems((prev) =>
          prev.map((e) => {
            if (e.id !== estId) return e;
            return {
              ...e,
              status: currentStatus,
              channels:
                nextStatus === false
                  ? e.channels.map((ch) => ({ ...ch, active: true }))
                  : e.channels,
            };
          })
        );
        toast.error(intl.formatMessage({ id: "messages.connectionError" }));
      }
    },
    [intl]
  );

  async function handleDeleteChannel(estId: string, channelId: string) {
    try {
      await api.delete(`/app/establishments/${estId}/channels/${channelId}`);
      toast.success(intl.formatMessage({ id: "cameras.deleteSuccess" }));
      // Remove from items
      setItems((prev) =>
        prev.map((e) =>
          e.id === estId
            ? { ...e, channels: e.channels.filter((ch) => ch.id !== channelId) }
            : e
        )
      );
    } catch (err) {
      const message =
        typeof (err as any)?.response?.data?.message === "string"
          ? (err as any).response.data.message
          : intl.formatMessage({ id: "messages.connectionError" });
      toast.error(message);
    }
  }

  async function handleDeleteDevice(
    estId: string,
    deviceId: number,
    channelId: string
  ) {
    try {
      await api.delete(`/app/cameras/${estId}/devices/${deviceId}`);
      toast.success(intl.formatMessage({ id: "cameras.deleteSuccess" }));
      setItems((prev) =>
        prev.map((e) =>
          e.id === estId
            ? {
                ...e,
                channels: e.channels.filter(
                  (ch) =>
                    !(
                      ch.id === channelId &&
                      Number.isFinite(Number(ch.deviceId)) &&
                      Number(ch.deviceId) === deviceId
                    )
                ),
              }
            : e
        )
      );
    } catch (err) {
      const message =
        typeof (err as any)?.response?.data?.message === "string"
          ? (err as any).response.data.message
          : intl.formatMessage({ id: "messages.connectionError" });
      toast.error(message);
    }
  }

  const onlineCountsByEstablishment = useMemo(() => {
    const counts: Record<string, { online: number; total: number }> = {};
    items.forEach((est) => {
      counts[est.id] = {
        online: est.channels.filter((ch) => ch.online !== false).length,
        total: est.channels.length,
      };
    });
    return counts;
  }, [items]);

  const visibleItems = useMemo(() => {
    if (!onlineFilter) return items;
    return items
      .map((est) => ({
        ...est,
        channels: est.channels.filter((ch) =>
          onlineFilter === "online" ? ch.online !== false : ch.online === false
        ),
      }))
      .filter((est) => est.channels.length > 0);
  }, [items, onlineFilter]);

  return (
    <Box>
      <PageHeader title={intl.formatMessage({ id: "cameras.title" })} />
      {/* Header: filters and pagination (admin-only) */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress />
              </Box>
            }
          >
            <CamerasFilters
              country={country}
              search={search}
              setSearch={setSearch}
              stateFilter={stateFilter}
              setStateFilter={setStateFilter}
              cityFilter={cityFilter}
              setCityFilter={setCityFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              onlineFilter={onlineFilter}
              setOnlineFilter={setOnlineFilter}
              sortValue={sortValue}
              setSortValue={setSortValue}
              isAdmin={canOperate}
              totalCount={totalCount}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Suspense>
        </Box>
      </Box>

      {canOperate && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <LoadingButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/estabelecimento/new")}
          >
            {intl.formatMessage({ id: "common.add" })}
          </LoadingButton>
        </Box>
      )}

      {/*
        Show full skeleton grid only on the first load.
        During refreshes with existing items, keep cards visible to avoid UI flicker.
      */}
      {loading && items.length === 0 && (
        <Fade in timeout={200}>
          <Grid container spacing={2}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card sx={{ p: 0 }}>
                  <Skeleton variant="rectangular" height={220} />
                  <Box sx={{ p: 1.5 }}>
                    <Skeleton width="60%" />
                    <Skeleton width="40%" />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Fade>
      )}

      {/* show results or no-results message */}
      {!loading && items.length === 0 ? (
        <Fade in timeout={200}>
          <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body1" sx={{ fontWeight: fontWeights.semiBold, mb: 0.5 }}>
              {intl.formatMessage({ id: "cameras.noCameras" })}
            </Typography>
            <Typography variant="body2">
              {intl.formatMessage({
                id: canOperate
                  ? "cameras.noCamerasHintOperator"
                  : "cameras.noCamerasHint",
              })}
            </Typography>
          </Box>
        </Fade>
      ) : !loading && visibleItems.length === 0 ? (
        <Fade in timeout={200}>
          <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <Typography variant="body1" sx={{ fontWeight: fontWeights.semiBold, mb: 0.5 }}>
              {intl.formatMessage({ id: "cameras.noCamerasForOnlineFilter" })}
            </Typography>
          </Box>
        </Fade>
      ) : visibleItems.length > 0 ? (
        visibleItems.map((est) => (
          <Box key={est.id} sx={{ mb: 6 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6">{est.name}</Typography>
                {onlineCountsByEstablishment[est.id] ? (
                  <StatusPill
                    size="small"
                    level={
                      onlineCountsByEstablishment[est.id].online ===
                      onlineCountsByEstablishment[est.id].total
                        ? "success"
                        : onlineCountsByEstablishment[est.id].online === 0
                        ? "error"
                        : "warning"
                    }
                    label={intl.formatMessage(
                      { id: "cameras.onlineCount" },
                      {
                        online: onlineCountsByEstablishment[est.id].online,
                        total: onlineCountsByEstablishment[est.id].total,
                      }
                    )}
                  />
                ) : null}
                {canOperate && (
                  <>
                    <IconButton
                      onClick={() => navigate(`/estabelecimento/${est.id}`)}
                      tooltip={intl.formatMessage({ id: "common.edit" })}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setConfirm({
                          type: "deleteEst",
                          id: est.id,
                          name: est.name,
                        });
                      }}
                      tooltip={intl.formatMessage({ id: "common.delete" })}
                      sx={{ color: "error.main", "&:hover": { color: "error.dark" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </>

                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  {est.state && est.city ? (
                    <>
                      <span>{est.state}</span>
                      <span>•</span>
                      <span>{est.city}</span>
                    </>
                  ) : null}

                  {est.isPrivate ? (
                    <strong style={{ marginLeft: 4 }}>
                      {intl.formatMessage({
                        id: "cameras.details.privateCourt",
                      })}
                    </strong>
                  ) : null}

                  {est.createdAt ? (
                    <span style={{ marginLeft: 8 }}>
                      {intl.formatMessage({ id: "common.createdAt" })}:{" "}
                      {new Date(est.createdAt).toLocaleDateString(intl.locale)}
                    </span>
                  ) : null}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="caption" sx={{ mr: 1 }}>
                    {est.status
                      ? intl.formatMessage({ id: "common.active" })
                      : intl.formatMessage({ id: "common.inactive" })}
                  </Typography>
                  <StatusToggle
                    checked={!!est.status}
                    disabled={!canOperate}
                    tooltip={intl.formatMessage({
                      id: "establishmentList.toggleStatus",
                    })}
                    confirmTitle={intl.formatMessage({
                      id: "establishmentList.toggleConfirmTitle",
                    })}
                    confirmMessage={intl.formatMessage(
                      { id: "establishmentList.toggleConfirmMessage" },
                      {
                        action: est.status
                          ? intl.formatMessage({ id: "establishmentList.disable" })
                          : intl.formatMessage({ id: "establishmentList.enable" }),
                        name: est.name || "-",
                      }
                    )}
                    onToggle={() =>
                      handleToggleEstablishmentStatus(est.id, !!est.status)
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Grid container spacing={2}>
              {est.channels.map((channel) => {
                const channelKey = getChannelKey(est.id, channel.id, channel.deviceId);
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    lg={3}
                    key={channel.deviceCardKey || channel.id}
                  >
                    <CameraCard
                      name={channel.name}
                      subtitle={channel.deviceLabel}
                      establishmentId={est.id}
                      channelId={channel.id}
                      thumbnailOnClick={() => {
                        if (!est.status || channel.online === false) return;
                        void openChannelPreviewModal(
                          est.id,
                          channel.id,
                          channel.name,
                          channel.deviceId
                        );
                      }}
                      onStatusChange={(next) => {
                        const prev = channel.active;
                        setItems((prevItems) =>
                          prevItems.map((e) =>
                            e.id === est.id
                              ? {
                                  ...e,
                                  channels: e.channels.map((ch) =>
                                    ch.id === channel.id
                                      ? { ...ch, active: next }
                                      : ch
                                  ),
                                }
                              : e
                          )
                        );
                        api.put(
                          `app/establishments/${est.id}/channels/${channel.id}/updateStatus`,
                          {}
                        ).then(() => {
                          toast.success(
                            intl.formatMessage({ id: "cameras.toggleSuccess" })
                          );
                        }).catch(() => {
                          setItems((prevItems) =>
                            prevItems.map((e) =>
                              e.id === est.id
                                ? {
                                    ...e,
                                    channels: e.channels.map((ch) =>
                                      ch.id === channel.id
                                        ? { ...ch, active: prev }
                                        : ch
                                    ),
                                  }
                                : e
                            )
                          );
                          toast.error(
                            intl.formatMessage({ id: "messages.connectionError" })
                          );
                        });
                      }}
                      active={channel.active}
                      disabled={!est.status}
                      isOffline={channel.online === false}
                      canPreview={channel.online !== false}
                      offlineSince={
                        channel.online === false ? channel.offlineSince : undefined
                      }
                      frameSrc={channel.frameSrc}
                      previewPlaying={playingChannelKey === channelKey}
                      previewLoading={loadingPreviewChannelKey === channelKey}
                      onStreamError={() => {
                        void handleChannelStreamError(
                          est.id,
                          channel.id,
                          channel.deviceId
                        );
                      }}
                      onOpenLegacyPreview={() => {
                        void openChannelLegacyPreview(est.id, channel.id);
                      }}
                      nameOnClick={() => {
                        const params = new URLSearchParams();
                        if (
                          Number.isFinite(Number(channel.deviceId)) &&
                          Number(channel.deviceId) > 0
                        ) {
                          params.set("deviceId", String(channel.deviceId));
                        }
                        if (String(channel.name ?? "").trim()) {
                          params.set("deviceName", String(channel.name).trim());
                        }
                        const query = params.toString();
                        const url = `/cameras/${est.id}/${channel.id}${
                          query ? `?${query}` : ""
                        }`;
                        if (canOperate) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                      channelCreatedAt={channel.createdAt}
                      channelUpdatedAt={channel.updatedAt}
                      toggleTooltip={intl.formatMessage({
                        id: canOperate
                          ? "cameras.toggleTooltip"
                          : "cameras.toggleTooltipAdmin",
                      })}
                      onDelete={
                        canOperate
                          ? () => {
                              const hasDevice =
                                Number.isFinite(Number(channel.deviceId)) &&
                                Number(channel.deviceId) > 0;
                              setConfirm(
                                hasDevice
                                  ? {
                                      type: "deleteDevice",
                                      estId: est.id,
                                      channelId: channel.id,
                                      deviceId: Number(channel.deviceId),
                                      name: channel.name,
                                    }
                                  : {
                                      type: "deleteChannel",
                                      estId: est.id,
                                      channelId: channel.id,
                                      name: channel.name,
                                    }
                              );
                            }
                          : undefined
                      }
                      deleteTooltip={intl.formatMessage({ id: "common.delete" })}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))
      ) : null}

      <Suspense
        fallback={
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        }
      >
        <CameraPreviewModal
          open={!!openChannelPreview}
          name={openChannelPreview?.name}
          hlsSrc={openChannelPreviewHlsSrc}
          startingHls={openChannelPreviewStartingHls}
          streamSrc={openChannelPreviewStreamSrc}
          frameSrc={
            openChannelPreview
              ? `/app/establishments/${openChannelPreview.estId}/channels/${openChannelPreview.channelId}/snapshot?liveOnly=1${
                  Number.isFinite(Number(openChannelPreview.deviceId)) &&
                  Number(openChannelPreview.deviceId) > 0
                    ? `&deviceId=${encodeURIComponent(String(openChannelPreview.deviceId))}`
                    : ""
                }`
              : undefined
          }
          onClose={closeChannelPreviewModal}
        />
      </Suspense>

      <ConfirmDialog
        open={confirm?.type === "deleteDevice"}
        title={intl.formatMessage({ id: "cameras.deleteDialog.titleDevice" })}
        message={intl.formatMessage(
          { id: "cameras.deleteDialog.messageDevice" },
          { name: confirm?.type === "deleteDevice" ? confirm.name : "-" }
        )}
        confirmLabel={intl.formatMessage({ id: "cameras.deleteDialog.confirm" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          if (confirm?.type !== "deleteDevice") return;
          await handleDeleteDevice(confirm.estId, confirm.deviceId, confirm.channelId);
          setConfirm(null);
        }}
      />

      <DeletionPreviewDialog
        open={confirm?.type === "deleteEst" || confirm?.type === "deleteChannel"}
        title={
          confirm?.type === "deleteEst"
            ? intl.formatMessage({ id: "cameras.deleteDialog.titleEstablishment" })
            : intl.formatMessage({ id: "cameras.deleteDialog.titleChannel" })
        }
        description={
          confirm?.type === "deleteEst"
            ? intl.formatMessage(
                { id: "deletionPreview.descriptionEstablishment" },
                { name: confirm.name }
              )
            : confirm?.type === "deleteChannel"
              ? intl.formatMessage(
                  { id: "deletionPreview.descriptionChannel" },
                  { name: confirm.name }
                )
              : ""
        }
        entityName={
          confirm?.type === "deleteEst" || confirm?.type === "deleteChannel"
            ? confirm.name
            : ""
        }
        loading={deletionSummary.loading}
        loadError={deletionSummary.error}
        rows={deletionPreviewRows}
        confirmLabel={intl.formatMessage({ id: "deletionPreview.confirm" })}
        cancelLabel={intl.formatMessage({ id: "common.cancel" })}
        onClose={() => setConfirm(null)}
        onConfirm={async () => {
          if (confirm?.type === "deleteEst") {
            await handleDeleteEstablishment(confirm.id);
          } else if (confirm?.type === "deleteChannel") {
            await handleDeleteChannel(confirm.estId, confirm.channelId);
          }
          setConfirm(null);
        }}
      />
    </Box>
  );
}
